import { z } from 'zod';

import {
  IdentifierSchema,
  IsoDateTimeSchema,
  LocalizedTextSchema,
  NonEmptyTextSchema,
  PositionSchema,
} from './common.js';

export const EditionProfileSchema = z
  .object({
    id: IdentifierSchema,
    editionId: IdentifierSchema,
    accountId: IdentifierSchema,
    displayName: NonEmptyTextSchema.max(60),
    avatarAssetId: IdentifierSchema.optional(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export const GuestPlanSchema = z
  .object({
    editionId: IdentifierSchema,
    actIds: z.array(IdentifierSchema),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export const PlanEntrySchema = z
  .object({
    editionId: IdentifierSchema,
    profileId: IdentifierSchema,
    actId: IdentifierSchema,
    createdAt: IsoDateTimeSchema,
  })
  .strict();

export const CrewRoleSchema = z.enum(['owner', 'moderator', 'member']);

export const CrewSchema = z
  .object({
    id: IdentifierSchema,
    editionId: IdentifierSchema,
    name: NonEmptyTextSchema.max(80),
    createdByProfileId: IdentifierSchema,
    inviteCode: z.string().min(8).max(128),
    inviteRevokedAt: IsoDateTimeSchema.optional(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export const CrewMembershipSchema = z
  .object({
    crewId: IdentifierSchema,
    editionId: IdentifierSchema,
    profileId: IdentifierSchema,
    role: CrewRoleSchema,
    joinedAt: IsoDateTimeSchema,
  })
  .strict();

export const CrewInvitationSchema = z
  .object({
    crewId: IdentifierSchema,
    editionId: IdentifierSchema,
    code: z.string().min(8).max(128),
    expiresAt: IsoDateTimeSchema,
    revokedAt: IsoDateTimeSchema.optional(),
  })
  .strict();

export const ChatMessageModerationStatusSchema = z.enum([
  'visible_pending',
  'visible',
  'under_review',
  'removed',
]);

const ChatMessageBaseShape = {
  id: IdentifierSchema,
  editionId: IdentifierSchema,
  crewId: IdentifierSchema,
  senderProfileId: IdentifierSchema.optional(),
  moderationStatus: ChatMessageModerationStatusSchema,
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema.optional(),
  clientMutationId: IdentifierSchema.optional(),
} as const;

export const ChatTextMessageSchema = z
  .object({
    ...ChatMessageBaseShape,
    kind: z.literal('text'),
    text: z.string().trim().min(1).max(4_000),
  })
  .strict();

export const ChatImageMessageSchema = z
  .object({
    ...ChatMessageBaseShape,
    kind: z.literal('image'),
    mediaId: IdentifierSchema,
    caption: z.string().trim().min(1).max(1_000).optional(),
  })
  .strict();

export const ChatSystemMessageSchema = z
  .object({
    ...ChatMessageBaseShape,
    kind: z.literal('system'),
    text: z.string().trim().min(1).max(4_000),
  })
  .strict();

export const ChatMessageSchema = z.discriminatedUnion('kind', [
  ChatTextMessageSchema,
  ChatImageMessageSchema,
  ChatSystemMessageSchema,
]);

export const ChatMediaSchema = z
  .object({
    id: IdentifierSchema,
    editionId: IdentifierSchema,
    crewId: IdentifierSchema,
    ownerProfileId: IdentifierSchema,
    mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp', 'image/heic']),
    byteSize: z.number().int().positive().max(15_000_000),
    width: z.number().int().positive().max(12_000),
    height: z.number().int().positive().max(12_000),
    status: z.enum(['pending', 'available', 'quarantined', 'deleted']),
    createdAt: IsoDateTimeSchema,
  })
  .strict();

export const MeetupStatusSchema = z.enum(['planned', 'cancelled', 'completed']);

export const MeetupMapFeatureLocationSchema = z
  .object({
    kind: z.literal('map_feature'),
    mapFeatureId: IdentifierSchema,
  })
  .strict();

export const MeetupCoordinateLocationSchema = z
  .object({
    kind: z.literal('coordinate'),
    position: PositionSchema,
    label: LocalizedTextSchema.optional(),
  })
  .strict();

export const MeetupTextLocationSchema = z
  .object({
    kind: z.literal('text'),
    label: LocalizedTextSchema,
  })
  .strict();

export const MeetupLocationSchema = z.discriminatedUnion('kind', [
  MeetupMapFeatureLocationSchema,
  MeetupCoordinateLocationSchema,
  MeetupTextLocationSchema,
]);

export const MeetupSchema = z
  .object({
    id: IdentifierSchema,
    editionId: IdentifierSchema,
    crewId: IdentifierSchema,
    createdByProfileId: IdentifierSchema,
    title: NonEmptyTextSchema.max(120),
    note: z.string().trim().min(1).max(1_000).optional(),
    startsAt: IsoDateTimeSchema,
    location: MeetupLocationSchema,
    status: MeetupStatusSchema,
    attendeeProfileIds: z.array(IdentifierSchema),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export const LocationShareDurationSchema = z.enum(['1h', '4h', '12h', 'edition_end']);
export const LocationShareStatusSchema = z.enum(['active', 'stopped', 'expired', 'revoked']);

export const LocationShareSessionSchema = z
  .object({
    id: IdentifierSchema,
    editionId: IdentifierSchema,
    crewId: IdentifierSchema,
    profileId: IdentifierSchema,
    duration: LocationShareDurationSchema,
    status: LocationShareStatusSchema,
    startsAt: IsoDateTimeSchema,
    expiresAt: IsoDateTimeSchema,
    stoppedAt: IsoDateTimeSchema.optional(),
  })
  .strict();

export const LocationPointSchema = z
  .object({
    sessionId: IdentifierSchema,
    position: PositionSchema,
    accuracyMeters: z.number().finite().nonnegative().max(10_000),
    recordedAt: IsoDateTimeSchema,
    sequence: z.number().int().nonnegative(),
  })
  .strict();

export const CrewLocationSchema = z
  .object({
    editionId: IdentifierSchema,
    crewId: IdentifierSchema,
    profileId: IdentifierSchema,
    sessionId: IdentifierSchema,
    point: LocationPointSchema,
    staleAfter: IsoDateTimeSchema,
  })
  .strict();

export const LocationUploadTokenClaimsSchema = z
  .object({
    sessionId: IdentifierSchema,
    editionId: IdentifierSchema,
    crewId: IdentifierSchema,
    profileId: IdentifierSchema,
    expiresAt: IsoDateTimeSchema,
    scope: z.literal('location:write'),
  })
  .strict();

export const PushPreferenceSchema = z
  .object({
    actReminders: z.boolean(),
    timetableChanges: z.boolean(),
    crewMessages: z.boolean(),
    meetups: z.boolean(),
  })
  .strict();

export const ModerationPresetSchema = z.enum(['loose', 'standard', 'strict']);
export const ModerationCategorySchema = z.enum([
  'sexual_minors',
  'sexual_adult',
  'threats',
  'violence',
  'self_harm',
  'drugs_trade',
  'drugs_dangerous_instructions',
  'hate',
  'harassment',
  'spam',
  'personal_data',
]);

export const ModerationScoreSchema = z.number().finite().min(0).max(1);
export const ModerationThresholdsSchema = z.record(ModerationCategorySchema, ModerationScoreSchema);

export const ModerationPolicyV1Schema = z
  .object({
    schemaVersion: z.literal(1),
    id: IdentifierSchema,
    version: z.number().int().positive(),
    preset: ModerationPresetSchema,
    thresholds: ModerationThresholdsSchema,
    imageUploadsEnabled: z.boolean(),
    updatedAt: IsoDateTimeSchema,
    updatedBy: IdentifierSchema,
  })
  .strict();

export const ModerationActionSchema = z.enum(['allow', 'review', 'remove']);

export const ModerationDecisionSchema = z
  .object({
    id: IdentifierSchema,
    editionId: IdentifierSchema,
    messageId: IdentifierSchema,
    policyVersion: z.number().int().positive(),
    action: ModerationActionSchema,
    scores: z.partialRecord(ModerationCategorySchema, ModerationScoreSchema),
    reasons: z.array(NonEmptyTextSchema.max(500)).max(20),
    provider: z.enum(['synchronous_filter', 'workers_ai', 'human']),
    decidedAt: IsoDateTimeSchema,
  })
  .strict();

export const ContentReportSchema = z
  .object({
    id: IdentifierSchema,
    editionId: IdentifierSchema,
    messageId: IdentifierSchema,
    reporterProfileId: IdentifierSchema,
    reason: z.enum(['harassment', 'hate', 'violence', 'sexual', 'drugs', 'spam', 'privacy', 'other']),
    note: z.string().trim().min(1).max(1_000).optional(),
    createdAt: IsoDateTimeSchema,
  })
  .strict();

export const MODERATION_MINIMUM_THRESHOLDS = {
  sexual_minors: 0.3,
  sexual_adult: 0.8,
  threats: 0.4,
  violence: 0.75,
  self_harm: 0.4,
  drugs_trade: 0.4,
  drugs_dangerous_instructions: 0.4,
  hate: 0.55,
  harassment: 0.75,
  spam: 0.9,
  personal_data: 0.8,
} as const satisfies z.infer<typeof ModerationThresholdsSchema>;

export type EditionProfile = z.infer<typeof EditionProfileSchema>;
export type GuestPlan = z.infer<typeof GuestPlanSchema>;
export type PlanEntry = z.infer<typeof PlanEntrySchema>;
export type CrewRole = z.infer<typeof CrewRoleSchema>;
export type Crew = z.infer<typeof CrewSchema>;
export type CrewMembership = z.infer<typeof CrewMembershipSchema>;
export type CrewInvitation = z.infer<typeof CrewInvitationSchema>;
export type ChatMessageModerationStatus = z.infer<typeof ChatMessageModerationStatusSchema>;
export type ChatTextMessage = z.infer<typeof ChatTextMessageSchema>;
export type ChatImageMessage = z.infer<typeof ChatImageMessageSchema>;
export type ChatSystemMessage = z.infer<typeof ChatSystemMessageSchema>;
export type ChatMessage = z.infer<typeof ChatMessageSchema>;
export type ChatMedia = z.infer<typeof ChatMediaSchema>;
export type MeetupStatus = z.infer<typeof MeetupStatusSchema>;
export type MeetupLocation = z.infer<typeof MeetupLocationSchema>;
export type Meetup = z.infer<typeof MeetupSchema>;
export type LocationShareDuration = z.infer<typeof LocationShareDurationSchema>;
export type LocationShareStatus = z.infer<typeof LocationShareStatusSchema>;
export type LocationShareSession = z.infer<typeof LocationShareSessionSchema>;
export type LocationPoint = z.infer<typeof LocationPointSchema>;
export type CrewLocation = z.infer<typeof CrewLocationSchema>;
export type LocationUploadTokenClaims = z.infer<typeof LocationUploadTokenClaimsSchema>;
export type PushPreference = z.infer<typeof PushPreferenceSchema>;
export type ModerationPreset = z.infer<typeof ModerationPresetSchema>;
export type ModerationCategory = z.infer<typeof ModerationCategorySchema>;
export type ModerationThresholds = z.infer<typeof ModerationThresholdsSchema>;
export type ModerationPolicyV1 = z.infer<typeof ModerationPolicyV1Schema>;
export type ModerationAction = z.infer<typeof ModerationActionSchema>;
export type ModerationDecision = z.infer<typeof ModerationDecisionSchema>;
export type ContentReport = z.infer<typeof ContentReportSchema>;
